import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react"
import { cn } from "@/lib/utils"

export type SortDirection = "asc" | "desc"

interface ThHeaderProps {
  label: string
  tooltip: string
  align?: "left" | "center" | "right"
  sticky?: boolean
  className?: string
  style?: React.CSSProperties
  /** Provide to make the column sortable. Sorting keyed by this string. */
  sortKey?: string
  activeSortKey?: string | null
  sortDirection?: SortDirection | null
  onSort?: (key: string) => void
}

/** Table header cell with a tooltip on hover. Must be inside a <TooltipProvider>. */
export function ThHeader({
  label,
  tooltip,
  align = "right",
  sticky,
  className,
  style,
  sortKey,
  activeSortKey,
  sortDirection,
  onSort,
}: ThHeaderProps) {
  const alignClass = align === "center" ? "text-center" : align === "left" ? "text-left" : "text-right"
  const isActive = sortKey !== undefined && activeSortKey === sortKey
  const sortable = sortKey !== undefined && onSort !== undefined

  const SortIcon = !sortable
    ? null
    : isActive
      ? sortDirection === "asc"
        ? ArrowUp
        : ArrowDown
      : ArrowUpDown

  const handleClick = () => {
    if (!sortable) return
    onSort(sortKey)
  }

  return (
    <th
      scope="col"
      aria-sort={
        sortable
          ? isActive
            ? sortDirection === "asc" ? "ascending" : "descending"
            : "none"
          : undefined
      }
      className={cn(
        "px-3 py-2 font-medium bg-muted sticky top-0",
        sticky ? "sticky left-0 z-30 text-left" : "z-20",
        className,
      )}
      style={style}
    >
      <div
        className={cn(
          "inline-flex items-center gap-1",
          align === "center" && "justify-center w-full",
          align === "right" && "justify-end w-full",
          sortable && "cursor-pointer select-none hover:text-foreground",
        )}
        onClick={sortable ? handleClick : undefined}
        role={sortable ? "button" : undefined}
        tabIndex={sortable ? 0 : undefined}
        onKeyDown={sortable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                handleClick()
              }
            }
          : undefined}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help border-b border-dotted border-muted-foreground/40">
              {label}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[220px] text-xs whitespace-pre-line">
            {tooltip}
          </TooltipContent>
        </Tooltip>
        {SortIcon && (
          <SortIcon
            className={cn(
              "h-3 w-3 shrink-0 transition-opacity",
              isActive ? "opacity-100" : "opacity-40",
            )}
            aria-hidden
          />
        )}
      </div>
    </th>
  )
}
